import request, { type RequestConfig } from './request'
export * from './learning'

export interface ProjectListResponse {
  items: Project[]
  total: number
}

export interface PaginatedListResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface Project {
  id: string | number
  title: string
  description?: string | null
  status: string
  genre?: string | null
  type?: string
  target_duration?: number | null
  progress?: number
  owner_id?: number
  current_episode?: number
  total_episodes?: number
  created_at: string
  updated_at?: string | null
  completed_at?: string | null
}

export interface CreateProjectRequest {
  title: string
  description?: string
  genre?: string
  target_duration?: number
  total_episodes?: number
}

export interface Scene {
  id: string
  project_id: string
  scene_number: number
  description: string
  characters: string[]
  location: string
  duration: number
  status: 'pending' | 'generating' | 'completed' | 'failed'
}

export interface Approval {
  id: string
  project_id: string
  type: 'script' | 'scene' | 'final_video'
  status: 'pending' | 'approved' | 'rejected'
  comment?: string
  media_url?: string
  created_at: string
}

export interface Material {
  id: number
  project_id: number
  scene_id?: number | null
  material_type: string
  name: string
  description?: string | null
  file_url?: string | null
  file_size?: number | null
  duration?: number | null
  status: string
  source?: string | null
  metadata?: Record<string, any> | null
  created_at: string
  updated_at: string
}

export const projectApi = {
  getList: (
    params?: { all_projects?: boolean; page?: number; page_size?: number; status?: string },
    config?: RequestConfig,
  ) => request.get<ProjectListResponse>('/projects', { ...config, params }),
  getById: (id: string | number) => request.get<Project>(`/projects/${id}`),
  create: (data: CreateProjectRequest) => request.post<Project>('/projects', data),
  update: (id: string | number, data: Partial<Project>) => request.put<Project>(`/projects/${id}`, data),
  delete: (id: string | number) => request.delete(`/projects/${id}`),
}

export const sceneApi = {
  getByProject: (projectId: string) => request.get<Scene[]>(`/projects/${projectId}/scenes`),
  getById: (id: string) => request.get<Scene>(`/scenes/${id}`),
  update: (id: string, data: Partial<Scene>) => request.put<Scene>(`/scenes/${id}`, data),
}

export const approvalApi = {
  getList: () => request.get<Approval[]>('/approvals'),
  getById: (id: string) => request.get<Approval>(`/approvals/${id}`),
  approve: (id: string, comment?: string) => request.post(`/approvals/${id}/approve`, { comment }),
  reject: (id: string, comment: string) => request.post(`/approvals/${id}/reject`, { comment }),
}

export const materialsApi = {
  list: (params?: {
    project_id?: number
    material_type?: string
    page?: number
    page_size?: number
  }) => request.get<PaginatedListResponse<Material>>('/materials', { params }),
  upload: (formData: FormData, params: { project_id: number; material_type: string; description?: string }) =>
    request.post<Material>('/materials/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    }),
  delete: (materialId: number) => request.delete(`/materials/${materialId}`),
  search: (projectId: number, query: string) =>
    request.get<Material[]>('/materials/search', { params: { project_id: projectId, q: query } }),
}
